import { throttle } from '@cian/newbuilding-utils';

import { clsx } from 'clsx';
import {
  Children,
  Component,
  type FC,
  type JSX,
  type PropsWithChildren,
  type MouseEvent as ReactMouseEvent,
  createRef,
} from 'react';

import { type IVerticalArrowProps, VerticalArrow } from '../VerticalArrow';

import { SCROLL_CLICK_DEAD_ZONE, SCROLL_TROTTLING_DELAY } from './constants';
import { getLeftScrollOffset, getRightScrollOffset } from './utils/getScrollOffsets';
import { scrollToPos } from './utils/scrollTo';

import styles from './styles.css';

export interface IVerticalSliderProps {
  arrowOffset?: number;
  arrow?: FC<IVerticalArrowProps>;
  containerClass?: string;
  onItemClick?(index: number): void;
}

export type TVerticalSliderProps = PropsWithChildren<IVerticalSliderProps>;

interface IVerticalSliderState {
  leftArrowVisible: boolean;
  rightArrowVisible: boolean;
}

export class VerticalSlider extends Component<TVerticalSliderProps, IVerticalSliderState> {
  private scrollRef = createRef<HTMLUListElement>();
  private startX = 0;
  private scrollLeft = 0;
  private isDragging = false;

  public state = {
    leftArrowVisible: false,
    rightArrowVisible: false,
  };

  public componentDidMount(): void {
    this.handleScroll();
  }

  public render(): JSX.Element {
    const { containerClass, arrowOffset, arrow: Arrow = VerticalArrow } = this.props;
    const { leftArrowVisible, rightArrowVisible } = this.state;

    return (
      <div className={styles['wrapper']}>
        <ul
          className={clsx(styles['container'], containerClass)}
          ref={this.scrollRef}
          onMouseDown={this.handleMouseDown}
          onScroll={this.throttledHandleScroll}
        >
          {Children.map(
            this.props.children,
            (child, i) =>
              child && (
                <li className={clsx(styles['item'])} key={i} onClick={this.handleItemClick(i)}>
                  {child}
                </li>
              ),
          )}
        </ul>
        {leftArrowVisible && <Arrow direction={-1} offset={arrowOffset} onClick={this.onLeftArrowClick} />}
        {rightArrowVisible && <Arrow direction={1} offset={arrowOffset} onClick={this.onRightArrowClick} />}
      </div>
    );
  }

  private handleMouseDown = (e: ReactMouseEvent): void => {
    e.preventDefault();

    const container = this.scrollRef.current;

    /* istanbul ignore else */
    if (container) {
      this.startX = e.pageX - container.offsetLeft;
      this.scrollLeft = container.scrollLeft;

      window.addEventListener('mousemove', this.handleMove);
      window.addEventListener('mouseup', this.handleMoveEnd);
    }

    this.isDragging = false;
  };

  private handleMoveEnd = (): void => {
    window.removeEventListener('mousemove', this.handleMove);
    window.removeEventListener('mousemove', this.handleMoveEnd);
  };

  private handleItemClick =
    (i: number) =>
    (e: ReactMouseEvent): void => {
      if (this.isDragging) {
        e.stopPropagation();

        return;
      }

      const { onItemClick } = this.props;

      /* istanbul ignore else */
      if (onItemClick) {
        onItemClick(i);
      }
    };

  private handleMove = (e: MouseEvent): void => {
    const container = this.scrollRef.current;

    /* istanbul ignore else */
    if (container) {
      if (Math.abs(e.pageX - this.startX) >= SCROLL_CLICK_DEAD_ZONE) {
        this.isDragging = true;
      }

      const x = e.pageX - container.offsetLeft;
      const walk = x - this.startX;

      container.scrollLeft = this.scrollLeft - walk;

      if (container.scrollLeft <= 0) {
        this.startX = x;
        this.scrollLeft = 0;
      }

      const hiddenWidth = container.scrollWidth - container.offsetWidth;

      if (container.scrollLeft >= hiddenWidth) {
        this.startX = x;
        this.scrollLeft = hiddenWidth;
      }
    }
  };

  private handleScroll = (): void => {
    const container = this.scrollRef.current;
    const { leftArrowVisible, rightArrowVisible } = this.state;

    /* istanbul ignore else */
    if (container) {
      const leftChild = container.firstChild as HTMLLIElement;
      const rightChild = container.lastChild as HTMLLIElement;

      const leftBorderPosition = container.scrollLeft;
      const rightBorderPosition = container.offsetWidth + container.scrollLeft;
      const showLeftArrow = leftChild.offsetLeft - container.offsetLeft < leftBorderPosition;
      const showRightArrow =
        rightChild.offsetLeft + rightChild.offsetWidth - container.offsetLeft > rightBorderPosition;

      if (leftArrowVisible !== showLeftArrow) {
        this.setState({ leftArrowVisible: showLeftArrow });
      }

      if (rightArrowVisible !== showRightArrow) {
        this.setState({ rightArrowVisible: showRightArrow });
      }
    }
  };

  private throttledHandleScroll = throttle(SCROLL_TROTTLING_DELAY, this.handleScroll);

  private onRightArrowClick = (): void => {
    const container = this.scrollRef.current;

    /* istanbul ignore else */
    if (container) {
      const scrollOffset = getRightScrollOffset(container) as number;

      scrollToPos(container, scrollOffset, 300);
    }
  };

  private onLeftArrowClick = (): void => {
    const container = this.scrollRef.current;

    /* istanbul ignore else */
    if (container) {
      const scrollOffset = getLeftScrollOffset(container) as number;

      scrollToPos(container, scrollOffset, 300);
    }
  };
}
